/**
 * Shared utility functions for working with product data.
 *
 * "Product" refers to a LocalProduct — a unit loaded from products.json,
 * optionally enriched by CRM data in later phases.
 */

import { AVAILABILITY_STATUS } from '../constants/availabilityStatus.js'

/**
 * Parses a raw price value into a valid positive number rounded to 2 decimal places,
 * or null if the value is missing/zero/non-numeric.
 *
 * Handles the following formats a Polish CRM may emit:
 *   - integer or float:                533250, 12.34, 0.5
 *   - numeric string (dot-decimal):    '533250', '12.34', '9.41e+000'
 *   - simple comma-decimal:            '32,95'  → 32.95
 *   - space-separated thousands:       '1 000 958,54' → 1000958.54
 *   - European full format:            '1.205,65' → 1205.65, '1.234.112,86' → 1234112.86
 *     (dot = thousands separator, comma = decimal — heuristic: both present)
 *
 * Ambiguous inputs:
 *   - '1.205' alone (no comma): treated as 1.205 (dot = decimal).
 *     Without locale info a dot-only string is indistinguishable from
 *     a US/EN decimal or a European-thousands integer.
 *
 * Returns null for: null, undefined, 0, negative values, NaN.
 *
 * @param {*} raw
 * @returns {number|null}
 */
function parsePrice(raw) {
  if (raw == null) return null
  let str = String(raw).trim()

  // 1. Strip whitespace used as thousands separator: '1 000 958,54' → '1000958,54'
  str = str.replace(/\s+/g, '')

  // 2. Normalize decimal separator.
  //    Heuristic: if string contains BOTH a dot AND a comma — European format
  //    where dot = thousands separator and comma = decimal separator.
  //    e.g. '1.205,65' → strip dots → '1205,65' → comma → dot → '1205.65'
  //    Without both characters, comma alone = decimal  (e.g. '32,95' → '32.95').
  //    Dot alone = decimal or scientific notation — leave for parseFloat.
  if (str.includes(',')) {
    if (str.includes('.')) {
      // European format: dot is thousands separator — strip all dots, then comma → dot
      str = str.replace(/\./g, '').replace(',', '.')
    } else {
      // Simple comma-decimal: '32,95' → '32.95'
      str = str.replace(',', '.')
    }
  }

  const n = parseFloat(str)
  if (isNaN(n) || n <= 0) return null
  return Math.round(n * 100) / 100
}

/**
 * Resolves the effective price of a product.
 *
 * Resolution order:
 *   1. Last entry in price_history (most recent price update from CRM/pipeline)
 *   2. price_brutto (static price baked into products.json)
 *
 * Returns null if neither source yields a valid positive number.
 * A price of 0 is treated as missing (same as null).
 * Result is rounded to 2 decimal places.
 *
 * @param {Object} product
 * @returns {number|null}
 */
export function resolvePrice(product) {
  const fromHistory = parsePrice(product.price_history?.at(-1)?.price_brutto)
  if (fromHistory !== null) return fromHistory
  return parsePrice(product.price_brutto)
}

/**
 * Returns false for any value that means "hidden" — covers all
 * formats the visibility field may arrive in (string, bool, number).
 *
 * Accepted truthy-visible values:   'visible', true, 1, '1', any non-empty string
 * Hidden or falsy values:            undefined, null, false, 0, '0', '', VISIBILITY_STATUS.HIDDEN
 *
 * @param {*} visibility
 * @returns {boolean}
 */
export function isDisplayable(visibility) {
  if (!visibility) return false
  if (visibility === 'hidden') return false
  if (visibility === 0 || visibility === '0' || visibility === false) return false
  return true
}

/**
 * Maps product status + panel accessibility settings to a public availability state.
 *
 * - `'inaccessible'` — sold/reserved unit blocked from public entry
 * - `'sold'` / `'reserved'` — still public but marked with business status
 * - `null` — available / unknown / any other status
 *
 * @param {object|null|undefined} product
 * @param {object|null|undefined} accessibility
 * @returns {'inaccessible'|'sold'|'reserved'|null}
 */
export function getProductAvailabilityState(product, accessibility) {
  if (!product) return null

  if (product.status === AVAILABILITY_STATUS.SOLD) {
    return accessibility?.soldUnitsAccessible === false ? 'inaccessible' : 'sold'
  }

  if (product.status === AVAILABILITY_STATUS.RESERVED) {
    return accessibility?.reservedUnitsAccessible === false ? 'inaccessible' : 'reserved'
  }

  return null
}

/**
 * Returns whether a product may be entered from public UI or deep links.
 *
 * @param {object|null|undefined} product
 * @param {object|null|undefined} accessibility
 * @returns {boolean}
 */
export function isProductAccessible(product, accessibility) {
  return getProductAvailabilityState(product, accessibility) !== 'inaccessible'
}

// ── Outline count helpers (used by SVG interaction hooks + tooltips) ──────────

/**
 * Returns true when a product is not sold and not on hold.
 * Reserved products ARE considered "not sold" (still on the market).
 *
 * @param {object} product — LocalProduct
 * @returns {boolean}
 */
export function isNotSold(product) {
  if (!product) return false
  const s = product.status
  return s !== AVAILABILITY_STATUS.SOLD && s !== AVAILABILITY_STATUS.HOLD
}

/**
 * Returns true only for products with status `available` or `unknown`.
 * Excludes sold, hold, AND reserved.
 *
 * @param {object} product — LocalProduct
 * @returns {boolean}
 */
export function isAvailable(product) {
  if (!product) return false
  const s = product.status
  return s !== AVAILABILITY_STATUS.SOLD
      && s !== AVAILABILITY_STATUS.HOLD
      && s !== AVAILABILITY_STATUS.RESERVED
}

/** TODO: is this "product" util?
 * Counts products on a specific ref_tier, optionally limited to one building.
 *
 * In bim-cadaplus a LocalProduct carries `ref_tier` (number) and `building_id`
 * directly — no nested `tiers` array to traverse.
 *
 * @param {object[]}     products       — LocalProduct[]
 * @param {number}       refTier        — tier number to count for
 * @param {string|null}  [buildingId]   — when provided, only products in that building
 * @param {boolean}      [countSold]    — default false — sold/hold excluded from count
 * @returns {number}
 */
export function countProductsOnTier(products, refTier, buildingId = null, countSold = false) {
  if (!Array.isArray(products) || refTier == null) return 0
  return products.reduce((n, p) => {
    // if (p.ref_tier !== refTier) return n
    const hasTiers = Array.isArray(p.tiers) && p.tiers.length > 0;
    const isOnTier = hasTiers
      ? p.tiers.some(tier => Number(tier?.ref_tier) === refTier)
      : Number(p?.ref_tier) === refTier;

    if (!isOnTier) return n;

    if (buildingId !== null && p.building_id !== buildingId) return n
    if (!countSold && !isNotSold(p)) return n
    return n + 1
  }, 0)
}

/** TODO: is this "product" util?
 * Counts products in a building across all tiers.
 *
 * @param {object[]}  products     — LocalProduct[]
 * @param {string}    buildingId   — building id_cad
 * @param {boolean}   [countSold]  — default false
 * @returns {number}
 */
export function countProductsInBuilding(products, buildingId, countSold = false) {
  if (!Array.isArray(products) || !buildingId) return 0
  return products.reduce((n, p) => {
    if (p.building_id !== buildingId) return n
    if (!countSold && !isNotSold(p)) return n
    return n + 1
  }, 0)
}


// TODO: is this good place for those utils? (used by Inventory- and Product- Contexts)
// add to index.js exports

function asArray(value) {
  return Array.isArray(value) ? value : []
}

function hasSunRanges(value) {
  return asArray(value).some((entry) => Array.isArray(entry) && entry.length === 2)
}

export function hasNamedCamera(tiers, matcher) {
  return asArray(tiers).some((tier) =>
    asArray(tier?.cameras).some((camera) => matcher(String(camera?.name ?? '').toLowerCase())),
  )
}

export function hasHotspotCameraAssets(tiers, interiorsData) {
  const interiors = interiorsData && typeof interiorsData === 'object' ? interiorsData : {}

  return asArray(tiers).some((tier) =>
    asArray(tier?.cameras).some((camera) => {
      const cameraName = String(camera?.name ?? '')
      if (!cameraName.toLowerCase().startsWith('interior_')) return false

      const files = interiors[cameraName]
      return Array.isArray(files) && files.length > 0
    }),
  )
}

export function getProductCompartments(product) {
  return asArray(product?.compartments)
}

export function getCompartmentVersionsCount(compartment) {
  const raw = compartment?.versions ?? compartment?.varsions ?? 0
  const parsed = Number(raw)
  return Number.isFinite(parsed) ? parsed : 0
}

export function hasCompartmentVariants(product) {
  return getProductCompartments(product).some((compartment) => getCompartmentVersionsCount(compartment) > 1)
}

// export function hasAnyProductCompartmentVariants(products) {
//   return asArray(products).some((item) => hasCompartmentVariants(item))
// }

export function hasInsolationData(product) {
  if (hasSunRanges(product?.sun_light_ranges)) return true

  const tiersHasSun = asArray(product?.tiers).some((tier) => {
    const windows = asArray(tier?.windows)
    return windows.some((windowItem) => hasSunRanges(windowItem?.sun_light_ranges))
  })
  if (tiersHasSun) return true

  return getProductCompartments(product).some((compartment) => hasSunRanges(compartment?.sun_light_ranges))
}